import type { ReactNode } from 'react';
import clsx from 'clsx';
import './CrateLabel.css';

/* ------------------------------------------------------------------ *
 * THE SIGNATURE ELEMENT.
 *
 * Every lot that moves through Mercabarna wears a printed label: a
 * bordered block with a colour band for the class of goods, a lot
 * code in mono, the producer in condensed caps, and a figures block —
 * calibre, count, net weight. Nothing on that label is decorative;
 * every mark is a fact somebody needs at 4am.
 *
 * Prody renders every entity as that label. An order, a product, a
 * shipment, an invoice and a purchase task are all the same block at
 * different scales: band, code, name, facts, figures, stamp. It is
 * the recurring unit of the whole product, it is honest to the
 * domain, and it is the thing that makes a screenshot of this app
 * unmistakable.
 *
 * The table row is this label flattened. The detail header is this
 * label enlarged. Learn it once, read it everywhere.
 * ------------------------------------------------------------------ */

interface CrateLabelProps {
  /** Handling class — drives the band. */
  handlingClass?: string;
  /** Mono code line: SKU, order number, lot code. */
  code?: ReactNode;
  title: ReactNode;
  /** Second line under the title: customer, producer, origin. */
  subtitle?: ReactNode;
  /** Small stencilled facts: origin, calibre, supplier, window. */
  facts?: { label: ReactNode; value: ReactNode }[];
  /** Right-hand figures block: total, quantity, balance. */
  figures?: ReactNode;
  /** Status stamp, top right. */
  stamp?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Interactive labels get hover and focus treatment. */
  onClick?: () => void;
  selected?: boolean;
  className?: string;
  children?: ReactNode;
}

export function CrateLabel({
  handlingClass = 'neutral',
  code,
  title,
  subtitle,
  facts,
  figures,
  stamp,
  size = 'md',
  onClick,
  selected,
  className,
  children,
}: CrateLabelProps) {
  const Root = onClick ? 'button' : 'article';
  return (
    <Root
      className={clsx('pd-crate', className)}
      data-hc={handlingClass}
      data-size={size}
      data-selected={selected || undefined}
      data-interactive={onClick ? true : undefined}
      onClick={onClick}
      {...(onClick ? { type: 'button' as const } : {})}
    >
      <span className="pd-band" aria-hidden="true" />

      <div className="pd-crate__body">
        <div className="pd-crate__top">
          {code ? <span className="num pd-crate__code">{code}</span> : null}
          {stamp ? <span className="pd-crate__stamp">{stamp}</span> : null}
        </div>

        <h3 className="pd-crate__title">{title}</h3>
        {subtitle ? <p className="pd-crate__subtitle truncate">{subtitle}</p> : null}

        {facts?.length ? (
          <dl className="pd-crate__facts">
            {facts.map((f, i) => (
              <div key={i} className="pd-crate__fact">
                <dt className="stencil">{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {children}
      </div>

      {figures ? <div className="pd-crate__figures">{figures}</div> : null}
    </Root>
  );
}

/**
 * The enlarged form used as a detail-panel header. Same anatomy, more
 * air, and it carries actions.
 */
export function CrateHeader({
  handlingClass = 'neutral',
  code,
  title,
  subtitle,
  stamp,
  facts,
  actions,
  className,
}: {
  handlingClass?: string;
  code?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  stamp?: ReactNode;
  facts?: { label: ReactNode; value: ReactNode }[];
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={clsx('pd-crate-head', className)} data-hc={handlingClass}>
      <span className="pd-band" aria-hidden="true" />
      <div className="pd-crate-head__body">
        <div className="pd-crate-head__top">
          <div className="row-baseline">
            {code ? <span className="num pd-crate-head__code">{code}</span> : null}
            {stamp}
          </div>
          {actions ? <div className="row" data-print="hide">{actions}</div> : null}
        </div>

        <h2 className="t-display pd-crate-head__title">{title}</h2>
        {subtitle ? <p className="pd-crate-head__subtitle">{subtitle}</p> : null}

        {facts?.length ? (
          <dl className="pd-crate-head__facts">
            {facts.map((f, i) => (
              <div key={i}>
                <dt className="stencil">{f.label}</dt>
                <dd className="pd-crate-head__fact-value">{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </header>
  );
}
